import { useQuery, useInfiniteQuery, UseQueryOptions } from '@tanstack/react-query';
import { RecipesService } from '@/services/recipes';
import { 
  Recipe,
  RecipeSearchParams
} from '@/types/api';

// Query keys for React Query
export const recipesKeys = {
  all: ['recipes'] as const,
  lists: () => [...recipesKeys.all, 'list'] as const,
  list: (params: RecipeSearchParams) => [...recipesKeys.lists(), params] as const,
  detail: (id: string) => [...recipesKeys.all, 'detail', id] as const,
  random: (count: number) => [...recipesKeys.all, 'random', count] as const,
  categories: () => [...recipesKeys.all, 'categories'] as const,
  areas: () => [...recipesKeys.all, 'areas'] as const,
  ingredients: () => [...recipesKeys.all, 'ingredients'] as const,
};

/**
 * Hook for searching recipes
 */
export function useRecipeSearch(params: RecipeSearchParams = {}) {
  return useQuery({
    queryKey: recipesKeys.list(params),
    queryFn: () => RecipesService.searchRecipes(params),
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
  });
}

/**
 * Hook for infinite scrolling recipe search
 */
export function useInfiniteRecipeSearch(params: Omit<RecipeSearchParams, 'page'>) {
  return useInfiniteQuery({
    queryKey: recipesKeys.list(params),
    queryFn: ({ pageParam = 1 }) => 
      RecipesService.searchRecipes({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for getting random recipes
 */
export function useRandomRecipes(count: number = 10, options?: Omit<UseQueryOptions<Recipe[], Error>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: recipesKeys.random(count),
    queryFn: () => RecipesService.getRandomRecipes(count),
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter for randomness)
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Hook for getting a specific recipe by ID
 */
export function useRecipe(id: string, options?: Omit<UseQueryOptions<Recipe, Error>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: recipesKeys.detail(id),
    queryFn: () => RecipesService.getRecipeById(id),
    enabled: !!id,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    ...options,
  });
}

/**
 * Hook for getting recipe categories
 */
export function useRecipeCategories() {
  return useQuery({
    queryKey: recipesKeys.categories(),
    queryFn: RecipesService.getCategories,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  });
}

/**
 * Hook for getting recipe areas/cuisines
 */
export function useRecipeAreas() {
  return useQuery({
    queryKey: recipesKeys.areas(),
    queryFn: RecipesService.getAreas,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  });
}

/**
 * Hook for getting available ingredients
 */
export function useRecipeIngredients() {
  return useQuery({
    queryKey: recipesKeys.ingredients(),
    queryFn: RecipesService.getIngredients,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  });
}

/**
 * Hook with multiple recipes by IDs (batch fetch)
 */
export function useRecipes(ids: string[], options?: Omit<UseQueryOptions<Recipe[], Error>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: [...recipesKeys.all, 'batch', ids.sort()],
    queryFn: async () => {
      const recipes = await Promise.all(
        ids.map(id => RecipesService.getRecipeById(id))
      );
      return recipes;
    },
    enabled: ids.length > 0,
    staleTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}
